/**
 * A KÉZI VÁGÁS GEOMETRIÁJA (#89) — a négy sarok, a fotó KÉPPONTJAIBAN.
 *
 * A vágó nézet (`CropView`) a teljes fotót mutatja, rajta négy húzható
 * sarokkal. Minden szám itt az EREDETI fotó koordinátáiban áll, nem a
 * kijelzőében: így a „Kész" pontosan azt vágja ki, amit az ügyfél látott,
 * akármekkora a telefon kijelzője, és az áttekintőről újra megnyitva a
 * sarkok ugyanott várnak.
 */

import type { QuadCorners } from './document-scanner';
import { isPlausiblePage } from './page-plausibility';

export type CornerName = keyof QuadCorners;

export const CORNER_NAMES: readonly CornerName[] = [
  'topLeft',
  'topRight',
  'bottomRight',
  'bottomLeft',
];

/**
 * A JAVASLAT, HA A FELISMERŐ NEM TALÁLT LAPOT: a kép széle, egy kicsit
 * beljebb. Nem a kép sarkai — azokat az ujj a képernyő szélén nehezen fogja
 * meg, és a lap a fotók többségén nem ér ki a kép széléig.
 */
export const DEFAULT_INSET = 0.06;

export function fullFrameCorners(width: number, height: number): QuadCorners {
  return {
    topLeft: { x: 0, y: 0 },
    topRight: { x: width, y: 0 },
    bottomRight: { x: width, y: height },
    bottomLeft: { x: 0, y: height },
  };
}

export function insetCorners(width: number, height: number, inset = DEFAULT_INSET): QuadCorners {
  const dx = width * inset;
  const dy = height * inset;
  return {
    topLeft: { x: dx, y: dy },
    topRight: { x: width - dx, y: dy },
    bottomRight: { x: width - dx, y: height - dy },
    bottomLeft: { x: dx, y: height - dy },
  };
}

/**
 * A VÁGÓ NÉZET KEZDŐ SARKAI: a felismerő javaslata, ha ésszerű lap (#88),
 * különben a beljebb húzott kép-széle. A felismerő a próbán megbízhatatlan
 * volt — ezért csak JAVASLAT, amit az ügyfél a „Kész" előtt lát.
 */
export function suggestedCorners(
  detected: QuadCorners | null,
  width: number,
  height: number
): QuadCorners {
  if (detected && isPlausiblePage(detected, width, height)) {
    return clampCorners(detected, width, height);
  }
  return insetCorners(width, height);
}

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max);

export function clampCorners(corners: QuadCorners, width: number, height: number): QuadCorners {
  const at = (point: { x: number; y: number }) => ({
    x: clamp(point.x, width),
    y: clamp(point.y, height),
  });
  return {
    topLeft: at(corners.topLeft),
    topRight: at(corners.topRight),
    bottomRight: at(corners.bottomRight),
    bottomLeft: at(corners.bottomLeft),
  };
}

/** Egy sarok új helye — a képen belül tartva. */
export function moveCorner(
  corners: QuadCorners,
  name: CornerName,
  to: { x: number; y: number },
  width: number,
  height: number
): QuadCorners {
  return { ...corners, [name]: { x: clamp(to.x, width), y: clamp(to.y, height) } };
}

/**
 * A TELJES KÉP-E — ilyenkor nincs mit kivágni, a fotó úgy megy, ahogy van.
 * Egy képpontnyi tűrés: a húzás tört koordinátái nem tesznek „vágottá" egy
 * olyan képet, amin az ügyfél nem vágott.
 */
export function isFullFrame(corners: QuadCorners, width: number, height: number): boolean {
  const full = fullFrameCorners(width, height);
  return CORNER_NAMES.every(
    (name) =>
      Math.abs(corners[name].x - full[name].x) <= 1 && Math.abs(corners[name].y - full[name].y) <= 1
  );
}
